// LAN-671: intro video watch depth on web.
//
// One `intro_video_watched` event per placement view session, flushed once when
// the placement exits (Continue click, modal close, page exit). The tracker
// counts DISTINCT integer seconds of the video, so a loop or a replay cannot
// inflate watch depth past the video's own length.
//
// The shared contract (same property names on mobile) lives in
// docs/analytics/posthog-events.md, "Intro video watch depth".
//
// CommonJS on purpose: the same file is required by the webpack packs and by
// `node --test` in test/javascript/account/video_watch_analytics_test.js.

const EVENT_NAME = 'intro_video_watched';
const SURFACE_WEB = 'web';
const PLATFORM_WEB = 'web';

const EXIT_PAGE = 'page_exit';
const EXIT_CONTINUE = 'continue';
const EXIT_CLOSED = 'closed';

const SEND_BEACON = { transport: 'sendBeacon' };

function isUsableNumber(value) {
  return typeof value === 'number' && isFinite(value) && value >= 0;
}

// Pure watch-depth accumulator. No DOM, no player, no analytics.
function createVideoWatchTracker() {
  const watchedSeconds = new Set();

  let duration = null;
  let started = false;
  let reachedEnd = false;
  let loops = 0;
  let muted = false;
  let lastTime = null;
  let seekBacks = 0;

  function cappedWatchSeconds() {
    if (duration === null) return watchedSeconds.size;
    return Math.min(watchedSeconds.size, Math.ceil(duration));
  }

  function watchPct() {
    if (duration === null || duration <= 0) return null;
    return Math.min(100, Math.round((100 * cappedWatchSeconds()) / duration));
  }

  return {
    setDuration(value) {
      if (isUsableNumber(value) && value > 0) duration = value;
    },

    setMuted(value) {
      muted = !!value;
    },

    // Every timeupdate lands here. A loop re-visits seconds already in the set,
    // so the set size stays the true watch depth.
    recordTime(currentTime) {
      if (!isUsableNumber(currentTime)) return;

      if (lastTime !== null && currentTime < lastTime - 1) seekBacks += 1;
      lastTime = currentTime;

      if (currentTime > 0) started = true;
      watchedSeconds.add(Math.floor(currentTime));
    },

    markStarted() {
      started = true;
    },

    // `loops` is the count of end events, per the contract. Web does not loop,
    // so this is 0 or 1 there; mobile loops and reports more.
    markEnded() {
      started = true;
      reachedEnd = true;
      loops += 1;
      lastTime = null;
    },

    snapshot() {
      return {
        duration_seconds: duration,
        watch_seconds: cappedWatchSeconds(),
        watch_pct: watchPct(),
        started: started,
        reached_end: reachedEnd,
        loops: loops,
        muted: muted
      };
    },

    // Test/debug only: how often playback jumped backwards by more than a
    // second. Not part of the event contract.
    seekBackCount() {
      return seekBacks;
    }
  };
}

function resolveWindow(explicit) {
  if (explicit) return explicit;
  return typeof window !== 'undefined' ? window : null;
}

function resolveDocument(explicit, win) {
  if (explicit) return explicit;
  if (win && win.document) return win.document;
  return typeof document !== 'undefined' ? document : null;
}

function defaultCapture(win) {
  return function capture(eventName, properties, captureOptions) {
    if (!win || !win.posthog || typeof win.posthog.capture !== 'function') return;
    win.posthog.capture(eventName, properties, captureOptions);
  };
}

function addListener(target, eventName, handler, registry) {
  if (target && typeof target.addEventListener === 'function') {
    target.addEventListener(eventName, handler);
    if (registry) registry.push({ target: target, eventName: eventName, handler: handler });
  }
}

function removeListeners(registry) {
  registry.splice(0).forEach(function (entry) {
    if (entry.target && typeof entry.target.removeEventListener === 'function') {
      entry.target.removeEventListener(entry.eventName, entry.handler);
    }
  });
}

// Wires a Cloudflare Stream player into a tracker and flushes exactly one
// event. `streamPlayer` is whatever `Stream(iframe)` returned.
function bindStreamPlayer(streamPlayer, options) {
  const settings = options || {};
  const win = resolveWindow(settings.windowRef);
  const doc = resolveDocument(settings.documentRef, win);
  const capture = settings.capture || defaultCapture(win);
  const tracker = settings.tracker || createVideoWatchTracker();

  const placement = settings.placement;
  const videoId = settings.videoId;

  let flushed = false;
  // Every listener this binding attaches, so destroy() can detach them. The
  // page keeps window/document across Turbolinks visits, and a binding from an
  // abandoned visit must not fire again on the next page's exit.
  const listeners = [];

  function addPlayerListener(eventName, handler) {
    addListener(streamPlayer, eventName, handler, listeners);
  }

  function readPlayerState() {
    if (!streamPlayer) return;
    tracker.setDuration(streamPlayer.duration);
    tracker.setMuted(streamPlayer.muted);
  }

  function flush(exit, captureOptions) {
    if (flushed) return false;
    flushed = true;

    readPlayerState();

    const properties = Object.assign(
      {
        surface: SURFACE_WEB,
        platform: PLATFORM_WEB,
        placement: placement,
        video_id: videoId
      },
      tracker.snapshot(),
      { exit: exit }
    );

    capture(EVENT_NAME, properties, captureOptions);
    return true;
  }

  addPlayerListener('loadedmetadata', readPlayerState);
  addPlayerListener('play', function () {
    tracker.setMuted(streamPlayer.muted);
  });
  addPlayerListener('pause', function () {
    tracker.setMuted(streamPlayer.muted);
  });
  addPlayerListener('volumechange', function () {
    tracker.setMuted(streamPlayer.muted);
  });
  addPlayerListener('timeupdate', function () {
    tracker.setDuration(streamPlayer.duration);
    tracker.setMuted(streamPlayer.muted);
    tracker.recordTime(streamPlayer.currentTime);
  });
  addPlayerListener('ended', function () {
    tracker.markEnded();
  });

  // Page exit. sendBeacon is the only transport that survives the unload.
  addListener(win, 'pagehide', function () {
    flush(EXIT_PAGE, SEND_BEACON);
  }, listeners);
  addListener(doc, 'visibilitychange', function () {
    if (doc && doc.visibilityState === 'hidden') flush(EXIT_PAGE, SEND_BEACON);
  }, listeners);

  // Registered exit controls: the welcome Continue link, the modal close button.
  const exitControls = Array.isArray(settings.exitControls) ? settings.exitControls : [];
  exitControls.forEach(function (control) {
    if (!control || !control.element) return;
    const exit = control.exit === EXIT_CLOSED ? EXIT_CLOSED : EXIT_CONTINUE;
    addListener(control.element, control.event || 'click', function () {
      flush(exit, SEND_BEACON);
    }, listeners);
  });

  // Detach everything this binding attached. Call it when the page that owns
  // the player is about to be replaced (Turbolinks before-render).
  function destroy() {
    removeListeners(listeners);
  }

  return {
    tracker: tracker,
    flush: flush,
    destroy: destroy,
    isFlushed: function () {
      return flushed;
    }
  };
}

module.exports = {
  EVENT_NAME: EVENT_NAME,
  createVideoWatchTracker: createVideoWatchTracker,
  bindStreamPlayer: bindStreamPlayer
};

// Inline view scripts reach the module through this global. The partial
// app/views/public/langua/_explainer_video.html.erb waits for it and warns once
// if the pack never loaded.
if (typeof window !== 'undefined') {
  window.LanguaVideoWatch = module.exports;
}
